"use client";

import { useCallback, useState } from "react";
import { useRouter } from "next/navigation";
import { useMutation } from "@tanstack/react-query";
import { useReCaptcha } from "next-recaptcha-v3";

import { showToast, TOAST_TYPES } from "@/lib/toast-utils/toast.util";
import { SendLoginLinkRequest } from "@/interface/auth.types";
import { sendLoginLink } from "@/services/auth.service";

const useMagicLink = () => {
  const router = useRouter();
  const { executeRecaptcha, loaded: recaptchaLoaded } = useReCaptcha();

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");

  const { mutate: requestLink, isPending } = useMutation({
    mutationFn: (data: SendLoginLinkRequest) => sendLoginLink(data),
    onSuccess: (data, variables) => {
      showToast(
        TOAST_TYPES.success,
        data?.message ?? "ログインリンクを送信しました"
      );
      setIsSubmitted(true);
      setErrorMessage("");
      // Navigate to check email page including email param
      router.push(
        `/login/check-email?email=${encodeURIComponent(variables.email)}`
      );
    },
    onError: (errors: string) => {
      setErrorMessage(errors);
      setIsSubmitted(false);
    },
  });

  const onSubmit = useCallback(
    async (values: { email: string }) => {
      if (!recaptchaLoaded) {
        showToast(
          TOAST_TYPES.error,
          "reCAPTCHAはまだ読み込み中です。再度お試しください。. Please try again."
        );
        return;
      }

      try {
        const token = await executeRecaptcha("magic_link_form_submit");
        requestLink({ email: values.email, captcha_v3_token: token });
      } catch (error) {
        console.error("reCAPTCHA error:", error);
        showToast(
          TOAST_TYPES.error,
          "reCAPTCHAの検証に失敗しました。再度お試しください。"
        );
      }
    },
    [executeRecaptcha, recaptchaLoaded, requestLink]
  );

  return {
    onSubmit,
    isLoading: isPending,
    recaptchaLoaded,
    isSubmitted,
    setIsSubmitted,
    errorMessage,
    setErrorMessage,
  };
};

export default useMagicLink;
